<template>
  <section class="main-trending">
    <header class="main-trending__head">
      <h2
        class="main-trending__heading text-head-2 text-sora text-bold mobile-font--h4"
      >
        {{ $t("main.m_trending_heading") }}
      </h2>
    </header>

    <ul class="main-trending__grid">
      <li v-for="item in topCountries" :key="item.code">
        <button
          type="button"
          class="main-trending__card"
          @click="onCountryClick(item.code)"
        >
          <span class="main-trending__flag">
            <base-icon
              :name="`flags/${flagSlug(item.code)}`"
              size="32px"
              filled
            />
          </span>
          <div class="main-trending__body">
            <span class="main-trending__name text-body-1 text-bold">
              {{ item.name }}
            </span>
            <span
              v-if="item.minApr !== null"
              class="main-trending__sub text-body-3"
            >
              {{ $t("main.m_country_from_apr", { apr: item.minApr.toFixed(1) }) }}
              ·
              {{ $t("main.m_country_deals", { n: formatCount(item.count) }) }}
            </span>
            <span v-else class="main-trending__sub text-body-3">
              {{ $t("main.m_country_deals", { n: formatCount(item.count) }) }}
            </span>
          </div>
          <span class="main-trending__chev" aria-hidden="true">→</span>
        </button>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { filtersStore } from "~/store/search_filters";
import { countryNames } from "~/utilities/constants/countryCodes";
import { useEstatePool } from "~/composables/useEstatePool";

const useFiltersStore = filtersStore();
const localePath = useLocalePath();
const { pool, ensure } = useEstatePool();

const TOP_LIMIT = 5;
const MIN_DEALS_FOR_APR = 3;

const FLAG_SLUG_OVERRIDES: Record<string, string> = {
  US: "united_states",
  GB: "united_kingdom",
  AE: "united_arab_emirates",
  KR: "south_korea",
  CZ: "czech_republic",
  BA: "bosnia_and_herzegovina",
  MK: "north_macedonia",
};

const flagSlug = (code: string) => {
  if (FLAG_SLUG_OVERRIDES[code]) return FLAG_SLUG_OVERRIDES[code];
  const name = countryNames[code];
  if (!name) return code.toLowerCase();
  return name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
};

const minAprByCountry = computed<Record<string, number>>(() => {
  const grouped: Record<string, number[]> = {};
  for (const estate of pool.value) {
    const code = estate.countryId;
    const apr = estate.apr;
    if (!code || typeof apr !== "number" || apr <= 0) continue;
    if (!grouped[code]) grouped[code] = [];
    grouped[code].push(apr);
  }
  const result: Record<string, number> = {};
  for (const code in grouped) {
    const values = grouped[code];
    if (values.length >= MIN_DEALS_FOR_APR) {
      result[code] = Math.min(...values);
    }
  }
  return result;
});

const topCountries = computed(() => {
  const stats = useFiltersStore.currentStats?.country || {};
  return Object.entries(stats)
    .filter(([code, count]) => code !== "null" && (count as number) > 0)
    .map(([code, count]) => ({
      code,
      name: countryNames[code] || code,
      count: count as number,
      minApr: minAprByCountry.value[code] ?? null,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_LIMIT);
});

const formatCount = (n: number) => new Intl.NumberFormat("en-US").format(n);

const onCountryClick = (code: string) => {
  navigateTo(
    localePath({ path: "/directory", query: { "countryId[0]": code } }),
  );
};

onMounted(() => {
  ensure();
});
</script>

<style scoped lang="scss">
.main-trending {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__heading {
    margin: 8px 0 0;
    color: var(--text-primary);
    letter-spacing: -0.5px;
  }

  &__grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  &__card {
    @include drop-button-styles;
    @include ds-card;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    width: 100%;
    text-align: left;
    cursor: pointer;

    &:hover {
      box-shadow: var(--shadow-card-hover);

      .main-trending__chev {
        color: var(--text-primary);
      }
    }

  }

  &__flag {
    display: inline-flex;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0;
    :deep(svg) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__name {
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chev {
    color: var(--text-secondary);
    font-size: var(--font-h3);
    line-height: 1;
    transition: transform 0.2s ease, color 0.2s ease;
  }
}

@media (max-width: 75rem) {
  .main-trending {
    gap: 16px;
    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@media (max-width: 32rem) {
  .main-trending {
    &__grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
